import type {
  GetStaticPropsContext,
  InferGetStaticPropsType,
  NextPage,
} from 'next';

import { getCollectionDocByPath, getCollectionDocByUid, getPageData } from '~/clients/firebase/admin';

import { Blog } from '~/clients/types';
import { parseMarkdown } from '~/lib/helpers/markdown';
import BlogLayout from '~/layouts/blog';
import PageLayout from '~/layouts/page';
import PromoLayout from '~/layouts/promo';
import { MasterCode, UserProfile } from 'types';

function redirectHome() {
  return {
    redirect: {
      destination: '/',
      permanent: false,
    },
    revalidate: 60 * 60 * 24 // every 24 hours
  };
}

const DynamicPage: NextPage<
  InferGetStaticPropsType<typeof getStaticProps>
> = (props) => {

  if (props.type === 'blog') return <BlogLayout post={props.post} />;

  if (props.type === 'page') return <PageLayout page={props.page} />;

  // otherwise it's a promo
  return <PromoLayout promo={props.promo as any} />;
};

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking'
  };
}

export async function getStaticProps(ctx: GetStaticPropsContext) {
  const pathArray = ctx.params.slug as string[];
  const pagePath = pathArray.map(p => p.toLowerCase()).join('/');
  const rootPath = pathArray[0]?.toLowerCase();

  // if root path is home,redirect home
  if (rootPath === 'home') return redirectHome();

  // Check for dynamic page existence
  const { page, components } = await getPageData(pagePath);
  if (page) {
    return {
      props: {
        type: 'page',
        page: {
          ...page,
          components,
        },
      },
      revalidate: 60 * 60 * 24 // every 24 hours
    };
  }

  // check for blog
  if (rootPath === 'blog') {
    const blogId = pathArray[1];
    if (!blogId) return redirectHome();

    const blogPost = await getCollectionDocByUid<Blog>('blog_posts', blogId);
    if (!blogPost) return redirectHome();

    const blogContent = parseMarkdown(blogPost.markdown);
    return {
      props: {
        type: 'blog',
        post: {
          ...blogPost,
          content: blogContent,
        }
      },
      revalidate: 60 * 60 * 24 // every 24 hours
    };
  }

  // will only be a promo at the root level
  if (pathArray.length > 1) return redirectHome();

  // If page is not found, check if it is a promo code
  const promoSlug = rootPath?.toUpperCase();
  let promo = await getCollectionDocByPath<MasterCode & { sellerData?: UserProfile; }>(
    `admin/promotions/codes/${promoSlug}`
  );
  promo = JSON.parse(JSON.stringify(promo));

  if (promo?.ambassador_uid) {
    promo.sellerData = await getCollectionDocByUid<UserProfile>('users', promo.ambassador_uid);
  }

  if (!promo) return redirectHome();

  return {
    props: {
      type: 'promo',
      promo
    },
    revalidate: 60 * 60 * 24 // every 24 hours
  };
}

export default DynamicPage;
